import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationContext,
  ValidationResult,
} from '../types.ts';
import { chromeArg, chromeArgs } from '../validation.ts';
import {
  errorText,
  formatError,
  numberValue,
  result,
  stringValue,
  validation,
  validationError,
  workerValue,
} from './shared.ts';

async function timezoneInWorker(): Promise<string> {
  const value = await workerValue(
    'Intl.DateTimeFormat().resolvedOptions().timeZone',
  );
  return typeof value == 'string' ? value : '';
}

async function collect(): Promise<RawData> {
  const iframe = document.createElement('iframe');
  iframe.hidden = true;
  document.body.append(iframe);
  try {
    const iframeWindow = iframe.contentWindow as
      (Window & { Intl: typeof Intl }) | null;
    const iframeIana =
      iframeWindow?.Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    return {
      iana: Intl.DateTimeFormat().resolvedOptions().timeZone,
      januaryOffsetMinutes: new Date(2025, 0, 15).getTimezoneOffset(),
      julyOffsetMinutes: new Date(2025, 6, 15).getTimezoneOffset(),
      dateText: new Intl.DateTimeFormat('ru-RU', {
        dateStyle: 'full',
        timeStyle: 'long',
      }).format(new Date(1_735_732_800_000)),
      intlText: new Intl.DateTimeFormat('ru-RU', {
        timeZoneName: 'long',
      }).format(new Date(1_735_732_800_000)),
      iframeIana,
      workerIana: await timezoneInWorker(),
    };
  } finally {
    iframe.remove();
  }
}

function validateTimezone(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const reasons: string[] = [];
  const iana = stringValue(rawData, 'iana');
  if (!iana || !iana.includes('/')) {
    reasons.push('Timezone is not a normal IANA identifier');
  } else {
    try {
      new Intl.DateTimeFormat('ru-RU', { timeZone: iana }).format();
    } catch {
      reasons.push(`${iana} is not a supported IANA timezone`);
    }
  }
  if (rawData.iframeIana !== iana || rawData.workerIana !== iana) {
    reasons.push('Timezone differs across realms');
  }
  for (const key of ['januaryOffsetMinutes', 'julyOffsetMinutes']) {
    const value = numberValue(rawData, key);
    if (value == null || Math.abs(value) > 840) {
      reasons.push(`${key} is outside the normal timezone range`);
    }
  }
  const args = chromeArgs(context);
  const configured = args
    ? (chromeArg(args, 'time-zone-for-testing') ?? chromeArg(args, 'timezone'))
    : null;
  if (configured && configured != iana) {
    reasons.push(`Timezone does not match ${configured}`);
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return String(rawData.iana);
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(
  rawData: RawData,
  context: ValidationContext,
): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateTimezone(rawData, context);
}

export const timezoneTest: TestDefinition = {
  id: 'timezone',
  title: 'Timezone',
  category: 'stableProfile',
  spoofingChromeArgs: ['time-zone-for-testing', 'timezone'],
  format,
  run,
  validate,
};
